import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { orpc } from "~/lib/orpc-client";
import { useToast } from "@alchema/ui";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/salary/")({
  component: SalaryBenchmarks,
});

const SENIORITY_OPTIONS = ["entry", "mid", "senior", "executive"] as const;
type Seniority = (typeof SENIORITY_OPTIONS)[number];

const SENIORITY_LABELS: Record<Seniority, () => string> = {
  entry: () => m.salary_seniority_entry(),
  mid: () => m.salary_seniority_mid(),
  senior: () => m.salary_seniority_senior(),
  executive: () => m.salary_seniority_executive(),
};

const REGION_PRESETS = [
  "EU (Germany)",
  "EU (Austria)",
  "EU (Switzerland)",
  "US (SF Bay)",
  "US (NYC)",
  "Remote Global",
];

interface SalaryRange {
  source: string;
  min: number;
  median: number;
  max: number;
  sampleSize: string;
}

interface BenchmarkData {
  role: string;
  location: string;
  seniority: string;
  currency: string;
  ranges: SalaryRange[];
  summary: string;
  factors: string[];
}

interface RegionalItem {
  region: string;
  currency: string;
  min: number;
  median: number;
  max: number;
  costOfLivingIndex: number;
  adjustedMedian: number;
}

function SalaryBenchmarks() {
  const { toast } = useToast();

  // Search form state
  const [role, setRole] = React.useState("");
  const [location, setLocation] = React.useState("");
  const [seniority, setSeniority] = React.useState<Seniority>("mid");

  // Results state
  const [benchmark, setBenchmark] = React.useState<BenchmarkData | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  // Regional comparison state
  const [showRegional, setShowRegional] = React.useState(false);
  const [selectedRegions, setSelectedRegions] = React.useState<string[]>([]);
  const [comparison, setComparison] = React.useState<RegionalItem[] | null>(null);
  const [isComparing, setIsComparing] = React.useState(false);

  async function handleSearch() {
    if (!role.trim() || !location.trim()) {
      toast(m.salary_search_error_fields(), "error");
      return;
    }
    setIsLoading(true);
    setBenchmark(null);
    setComparison(null);
    try {
      const result = await orpc.salary.benchmark.call({ role, location, seniority });
      setBenchmark(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : m.salary_search_error_fetch();
      toast(message, "error");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCompare() {
    if (selectedRegions.length < 2) {
      toast(m.salary_compare_error_min(), "error");
      return;
    }
    setIsComparing(true);
    try {
      const result = await orpc.salary.compare.call({
        role: benchmark?.role || role,
        seniority,
        regions: selectedRegions,
      });
      setComparison(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : m.salary_compare_error_fetch();
      toast(message, "error");
    } finally {
      setIsComparing(false);
    }
  }

  function toggleRegion(region: string) {
    setSelectedRegions((prev) =>
      prev.includes(region)
        ? prev.filter((r) => r !== region)
        : prev.length < 5
          ? [...prev, region]
          : prev,
    );
  }

  function formatCurrency(value: number, currency: string) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.length === 3 ? currency : "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  }

  // Calculate bar width percentage for range visualization
  function barPercent(value: number, min: number, max: number) {
    if (max === min) return 50;
    return ((value - min) / (max - min)) * 100;
  }

  // Global median for color-coding regional comparison
  const globalMedian =
    comparison && comparison.length > 0
      ? comparison.reduce((sum, c) => sum + c.adjustedMedian, 0) / comparison.length
      : 0;

  return (
    <div className="space-y-6">
      {/* Search Form */}
      <div
        className="rounded-xl p-5"
        style={{ border: "1px solid var(--border-subtle)", background: "var(--surface-raised)" }}
      >
        <p className="section-label">{m.salary_search_title()}</p>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium" style={{ color: "var(--text-muted)" }}>
              {m.salary_search_role()}
            </label>
            <input
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder={m.salary_search_role_placeholder()}
              className="w-full rounded-lg px-3 py-2 text-sm outline-none transition-colors focus:ring-1 focus:ring-[var(--accent)]"
              style={{
                background: "var(--surface-raised)",
                border: "1px solid var(--border-subtle)",
                color: "var(--text)",
              }}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium" style={{ color: "var(--text-muted)" }}>
              {m.salary_search_location()}
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={m.salary_search_location_placeholder()}
              className="w-full rounded-lg px-3 py-2 text-sm outline-none transition-colors focus:ring-1 focus:ring-[var(--accent)]"
              style={{
                background: "var(--surface-raised)",
                border: "1px solid var(--border-subtle)",
                color: "var(--text)",
              }}
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="mb-1.5 block text-xs font-medium" style={{ color: "var(--text-muted)" }}>
            {m.salary_search_seniority()}
          </label>
          <div className="flex flex-wrap gap-2">
            {SENIORITY_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setSeniority(s)}
                className="rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-150"
                style={
                  seniority === s
                    ? { background: "var(--accent)", color: "#fff", border: "1px solid var(--accent)" }
                    : {
                        background: "transparent",
                        border: "1px solid var(--border-subtle)",
                        color: "var(--text-muted)",
                      }
                }
              >
                {SENIORITY_LABELS[s]()}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <button
            onClick={handleSearch}
            disabled={isLoading}
            className="rounded-lg px-5 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ background: "var(--accent)", color: "#fff" }}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {m.salary_search_analyzing()}
              </span>
            ) : (
              m.salary_search_cta()
            )}
          </button>
        </div>
      </div>

      {/* Results */}
      {benchmark && (
        <div className="space-y-4 animate-fade-up">
          {/* AI Disclaimer */}
          <div
            className="flex items-center gap-2 rounded-lg px-3 py-2"
            style={{
              border: "1px solid color-mix(in oklch, var(--warning) 25%, transparent)",
              background: "color-mix(in oklch, var(--warning) 8%, transparent)",
            }}
          >
            <svg className="h-4 w-4 shrink-0" style={{ color: "var(--warning)" }} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <span className="text-xs font-medium" style={{ color: "var(--warning)" }}>
              {m.salary_disclaimer()}
            </span>
          </div>

          {/* Summary */}
          <div
            className="rounded-xl p-5"
            style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
          >
            <h3 className="text-sm font-semibold" style={{ color: "var(--text)" }}>
              {benchmark.seniority.charAt(0).toUpperCase() + benchmark.seniority.slice(1)} {benchmark.role} in {benchmark.location}
            </h3>
            <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--text-muted)" }}>
              {benchmark.summary}
            </p>
          </div>

          {/* Salary Ranges Table */}
          <div
            className="rounded-xl overflow-hidden"
            style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
          >
            <div className="px-5 py-3" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
              <p className="section-label">{m.salary_ranges_title({ currency: benchmark.currency })}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                    <th className="px-5 py-2.5 text-[11px] font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{m.salary_col_source()}</th>
                    <th className="px-5 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{m.salary_col_min()}</th>
                    <th className="px-5 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{m.salary_col_median()}</th>
                    <th className="px-5 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{m.salary_col_max()}</th>
                    <th className="px-5 py-2.5 text-[11px] font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{m.salary_col_range()}</th>
                    <th className="px-5 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{m.salary_col_sample()}</th>
                  </tr>
                </thead>
                <tbody>
                  {benchmark.ranges.map((range) => {
                    const globalMin = Math.min(...benchmark.ranges.map((r) => r.min));
                    const globalMax = Math.max(...benchmark.ranges.map((r) => r.max));
                    return (
                      <tr
                        key={range.source}
                        className="transition-colors hover:bg-[var(--surface-raised)]"
                        style={{ borderBottom: "1px solid var(--border-subtle)" }}
                      >
                        <td className="px-5 py-3 text-sm font-medium" style={{ color: "var(--text)" }}>{range.source}</td>
                        <td className="px-5 py-3 text-right text-sm" style={{ color: "var(--text-muted)" }}>
                          {formatCurrency(range.min, benchmark.currency)}
                        </td>
                        <td className="px-5 py-3 text-right text-sm font-semibold" style={{ color: "var(--text)" }}>
                          {formatCurrency(range.median, benchmark.currency)}
                        </td>
                        <td className="px-5 py-3 text-right text-sm" style={{ color: "var(--text-muted)" }}>
                          {formatCurrency(range.max, benchmark.currency)}
                        </td>
                        <td className="px-5 py-3">
                          <div className="relative h-2 w-32 rounded-full" style={{ background: "var(--border-subtle)" }}>
                            <div
                              className="absolute top-0 h-full rounded-full"
                              style={{
                                background: "color-mix(in oklch, var(--accent) 40%, transparent)",
                                left: `${barPercent(range.min, globalMin, globalMax)}%`,
                                width: `${barPercent(range.max, globalMin, globalMax) - barPercent(range.min, globalMin, globalMax)}%`,
                              }}
                            />
                            <div
                              className="absolute top-0 h-full w-0.5"
                              style={{ background: "var(--accent)", left: `${barPercent(range.median, globalMin, globalMax)}%` }}
                            />
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right text-xs" style={{ color: "var(--text-muted)" }}>{range.sampleSize}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Key Factors */}
          <div
            className="rounded-xl p-5"
            style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
          >
            <p className="section-label">{m.salary_factors_title()}</p>
            <ul className="mt-3 space-y-1.5">
              {benchmark.factors.map((factor, i) => (
                <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full" style={{ background: "var(--border-subtle)" }} />
                  {factor}
                </li>
              ))}
            </ul>
          </div>

          {/* Regional Comparison */}
          <div
            className="rounded-xl overflow-hidden"
            style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
          >
            <button
              onClick={() => setShowRegional(!showRegional)}
              className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-[var(--surface-raised)]"
            >
              <p className="section-label">{m.salary_compare_title()}</p>
              <svg
                className={`h-4 w-4 transition-transform duration-200 ${showRegional ? "rotate-180" : ""}`}
                style={{ color: "var(--text-muted)" }}
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </button>

            {showRegional && (
              <div className="px-5 py-4" style={{ borderTop: "1px solid var(--border-subtle)" }}>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>{m.salary_compare_hint()}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {REGION_PRESETS.map((region) => (
                    <button
                      key={region}
                      onClick={() => toggleRegion(region)}
                      className="rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-150"
                      style={
                        selectedRegions.includes(region)
                          ? { background: "var(--accent)", color: "#fff", border: "1px solid var(--accent)" }
                          : {
                              background: "transparent",
                              border: "1px solid var(--border-subtle)",
                              color: "var(--text-muted)",
                            }
                      }
                    >
                      {region}
                    </button>
                  ))}
                </div>

                <div className="mt-4">
                  <button
                    onClick={handleCompare}
                    disabled={isComparing || selectedRegions.length < 2}
                    className="rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{ background: "var(--accent)", color: "#fff" }}
                  >
                    {isComparing ? m.salary_compare_comparing() : m.salary_compare_cta()}
                  </button>
                </div>

                {comparison && (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                          <th className="py-2.5 pr-4 text-[11px] font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{m.salary_compare_col_region()}</th>
                          <th className="py-2.5 pr-4 text-[11px] font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{m.salary_compare_col_currency()}</th>
                          <th className="py-2.5 pr-4 text-right text-[11px] font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{m.salary_col_min()}</th>
                          <th className="py-2.5 pr-4 text-right text-[11px] font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{m.salary_col_median()}</th>
                          <th className="py-2.5 pr-4 text-right text-[11px] font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{m.salary_col_max()}</th>
                          <th className="py-2.5 pr-4 text-right text-[11px] font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{m.salary_compare_col_col_index()}</th>
                          <th className="py-2.5 text-right text-[11px] font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{m.salary_compare_col_adjusted()}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparison.map((item) => (
                          <tr
                            key={item.region}
                            className="transition-colors hover:bg-[var(--surface-raised)]"
                            style={{ borderBottom: "1px solid var(--border-subtle)" }}
                          >
                            <td className="py-3 pr-4 font-medium" style={{ color: "var(--text)" }}>{item.region}</td>
                            <td className="py-3 pr-4" style={{ color: "var(--text-muted)" }}>{item.currency}</td>
                            <td className="py-3 pr-4 text-right" style={{ color: "var(--text-muted)" }}>
                              {formatCurrency(item.min, item.currency)}
                            </td>
                            <td className="py-3 pr-4 text-right font-semibold" style={{ color: "var(--text)" }}>
                              {formatCurrency(item.median, item.currency)}
                            </td>
                            <td className="py-3 pr-4 text-right" style={{ color: "var(--text-muted)" }}>
                              {formatCurrency(item.max, item.currency)}
                            </td>
                            <td className="py-3 pr-4 text-right" style={{ color: "var(--text-muted)" }}>{item.costOfLivingIndex}</td>
                            <td className="py-3 text-right">
                              <span
                                className="font-semibold"
                                style={{ color: item.adjustedMedian >= globalMedian ? "var(--success)" : "var(--error)" }}
                              >
                                ${item.adjustedMedian.toLocaleString()}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
